import { BlogArticleMetadata, DocumentMetadata } from '@bifrost-dev-docs-mfe/common/types/api';
import { DefaultLocale, supportedLocales } from '@/plugins/i18n/locales';
import { getUrlForCurrenPage } from '@/utils/url';
import { ApplicationCardImage, ApplicationDescription, ApplicationTitle } from '@/constants';
import { setOpenGraphProperties, updateOpenGraphProperties } from './meta';

export const OpenGraph = {
  Title: 'og:title',
  SiteName: 'og:site_name',
  Description: 'og:description',
  Url: 'og:url',
  Type: 'og:type',
  Image: 'og:image',
  ImageWidth: 'og:image:width',
  ImageHeight: 'og:image:height',
  DefaultLocale: 'og:locale',
  AlternateLocale: 'og:locale:alternate',
} as const;

export enum OpenGraphPageType {
  Generic,
  Document,
  BlogArticle,
  Tutorial,
  ApiSpec,
  NoOpenGraphData = -1,
}

type OpenGraphPropertyName = typeof OpenGraph[keyof typeof OpenGraph];
type OpenGraphProperty = [OpenGraphPropertyName, string];
type OpenGraphProperties = OpenGraphProperty[];
type OpenGraphPropertyMap = { [key in OpenGraphPropertyName]?: string };

const ApplicationProperties: OpenGraphProperties = [
  [OpenGraph.SiteName, ApplicationTitle],
  ...supportedLocales.map(
    ({ locale, lang }): OpenGraphProperty => [
      locale === DefaultLocale ? OpenGraph.DefaultLocale : OpenGraph.AlternateLocale,
      lang,
    ]
  ),
];

const DefaultPageProperties: OpenGraphPropertyMap = {
  // we use article for normal pages as well to show the image
  [OpenGraph.Type]: 'article',
  [OpenGraph.Title]: ApplicationTitle,
  [OpenGraph.Description]: ApplicationDescription,
  [OpenGraph.Image]: ApplicationCardImage.Url,
  [OpenGraph.ImageWidth]: ApplicationCardImage.Width,
  [OpenGraph.ImageHeight]: ApplicationCardImage.Height,
};

const propertiesFromMap = (map: OpenGraphPropertyMap): OpenGraphProperties =>
  Object.entries(map) as OpenGraphProperties;

export function setApplicationOGTags() {
  // set open graph props
  setOpenGraphProperties(ApplicationProperties);
}

function setOGTagsForType(properties: OpenGraphProperties) {
  updateOpenGraphProperties([
    // we need to compute this every time
    [OpenGraph.Url, getUrlForCurrenPage()],
    ...properties,
  ]);
}

const propertiesFromDocumentMetadata = (
  pageTitle: string,
  { description, image }: DocumentMetadata | BlogArticleMetadata
) =>
  propertiesFromMap({
    ...DefaultPageProperties,
    [OpenGraph.Title]: pageTitle,
    ...(description ? { [OpenGraph.Description]: description } : {}),
    ...(image ? { [OpenGraph.Image]: image } : {}),
  });

type MetadataType<T> = T extends OpenGraphPageType.BlogArticle ? BlogArticleMetadata : DocumentMetadata;

export async function setPageOGTags<T extends OpenGraphPageType>(
  type: T,
  pageTitle: string,
  metadata: MetadataType<T>
) {
  setOGTagsForType(propertiesFromDocumentMetadata(pageTitle, metadata));
}
